import { GameMap } from "./gamemap";
import type { World } from "./world";
import { KIND_BUILDING, KIND_WRECKAGE, KIND_FEATURE } from "./constants";

// Flow-field pathfinding (GDD §9.3). One integration field is computed per
// destination cell and shared by every unit ordered to that area; per-unit local
// avoidance (in movement.ts) handles unit-vs-unit on top. The field routes around
// SOLID obstacles too: buildings, wreckage and map features are stamped into a
// "blocked" overlay so the field flows around them instead of straight through.
//
// Determinism: fields are a pure function of (static map, blocked overlay, goal
// cell). The overlay is derived deterministically from world state, and the whole
// field cache is cleared whenever that overlay changes (detected by a stable
// signature), so a sampled field is always consistent with the current obstacles.
// Recompute is lazy per goal, so a clear only re-derives the few fields in use.

const SQRT2 = Math.SQRT2;
const SLOPE_PENALTY = 6; // extra cost per unit of elevation climbed
const BLOCK_INFLATE = 3; // obstacle radius padding so units get a little clearance

/** Does this entity kind block the flow field? Under-construction buildings are
 * NOT blocked — a builder has to be able to path in to work on them. */
function blocksField(w: World, e: number): boolean {
  const k = w.kind[e];
  if (k === KIND_WRECKAGE || k === KIND_FEATURE) return true;
  if (k === KIND_BUILDING) return !w.underConstruction(e);
  return false;
}

const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;
function mixHash(h: number, v: number): number {
  h ^= v | 0;
  return Math.imul(h, FNV_PRIME) >>> 0;
}

// Minimal binary min-heap over cell indices keyed by cost.
class MinHeap {
  private items: number[] = [];
  private cost: Float32Array;
  constructor(cost: Float32Array) {
    this.cost = cost;
  }
  clear(): void {
    this.items.length = 0;
  }
  get size(): number {
    return this.items.length;
  }
  push(i: number): void {
    const a = this.items;
    a.push(i);
    let c = a.length - 1;
    while (c > 0) {
      const p = (c - 1) >> 1;
      if (this.cost[a[p]] <= this.cost[a[c]]) break;
      [a[p], a[c]] = [a[c], a[p]];
      c = p;
    }
  }
  pop(): number {
    const a = this.items;
    const top = a[0];
    const last = a.pop()!;
    if (a.length > 0) {
      a[0] = last;
      let p = 0;
      const n = a.length;
      for (;;) {
        const l = p * 2 + 1;
        const r = l + 1;
        let s = p;
        if (l < n && this.cost[a[l]] < this.cost[a[s]]) s = l;
        if (r < n && this.cost[a[r]] < this.cost[a[s]]) s = r;
        if (s === p) break;
        [a[p], a[s]] = [a[s], a[p]];
        p = s;
      }
    }
    return top;
  }
}

export class FlowField {
  readonly goalCx: number;
  readonly goalCy: number;
  readonly dirX: Float32Array;
  readonly dirY: Float32Array;
  readonly cost: Float32Array;
  lastUsedTick = 0;

  constructor(map: GameMap, goalCx: number, goalCy: number, blocked: Uint8Array) {
    this.goalCx = goalCx;
    this.goalCy = goalCy;
    const n = map.cols * map.rows;
    this.dirX = new Float32Array(n);
    this.dirY = new Float32Array(n);
    this.cost = new Float32Array(n).fill(Infinity);
    this.compute(map, blocked);
  }

  private compute(map: GameMap, blocked: Uint8Array): void {
    const { cols, rows, heights } = map;
    const cost = this.cost;
    const heap = new MinHeap(cost);
    const goal = this.goalCy * cols + this.goalCx;
    // A cell is passable if it is static-walkable and not obstacle-blocked. The
    // goal cell itself is always allowed as the source, so a field still forms
    // even when the destination sits on/under an obstacle.
    const passable = (i: number): boolean => map.walkable[i] !== 0 && (blocked[i] === 0 || i === goal);
    cost[goal] = 0;
    heap.push(goal);
    while (heap.size > 0) {
      const u = heap.pop();
      const ucx = u % cols;
      const ucy = (u / cols) | 0;
      const ucost = cost[u];
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dy === 0) continue;
          const nx = ucx + dx;
          const ny = ucy + dy;
          if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
          const ni = ny * cols + nx;
          if (!passable(ni)) continue;
          // Prevent cutting diagonally through wall / obstacle corners.
          if (dx !== 0 && dy !== 0) {
            if (!passable(ucy * cols + nx) || !passable(ny * cols + ucx)) continue;
          }
          const base = dx !== 0 && dy !== 0 ? SQRT2 : 1;
          const slope = Math.abs(heights[ni] - heights[u]) * SLOPE_PENALTY;
          const nc = ucost + base + slope;
          if (nc < cost[ni]) {
            cost[ni] = nc;
            heap.push(ni);
          }
        }
      }
    }
    // Build direction field: each cell points to its lowest-cost neighbour.
    for (let cy = 0; cy < rows; cy++) {
      for (let cx = 0; cx < cols; cx++) {
        const i = cy * cols + cx;
        if (map.walkable[i] === 0 || !isFinite(cost[i])) continue;
        let best = cost[i];
        let bx = 0;
        let by = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = cx + dx;
            const ny = cy + dy;
            if (nx < 0 || ny < 0 || nx >= cols || ny >= rows) continue;
            const ni = ny * cols + nx;
            if (!isFinite(cost[ni])) continue;
            if (cost[ni] < best) {
              best = cost[ni];
              bx = dx;
              by = dy;
            }
          }
        }
        if (bx !== 0 || by !== 0) {
          const inv = 1 / Math.hypot(bx, by);
          this.dirX[i] = bx * inv;
          this.dirY[i] = by * inv;
        }
      }
    }
  }

  /** Flow direction at a world position. Returns [0,0] at/near the goal. */
  sample(map: GameMap, x: number, y: number, out: { x: number; y: number }): void {
    const cx = map.worldToCellX(x);
    const cy = map.worldToCellY(y);
    const i = cy * map.cols + cx;
    out.x = this.dirX[i];
    out.y = this.dirY[i];
  }
}

export class FlowFieldManager {
  private map: GameMap;
  private cache = new Map<number, FlowField>();
  private cap = 48;
  private blocked: Uint8Array;
  private blockedSig = 0;
  private builtOnce = false;

  constructor(map: GameMap) {
    this.map = map;
    this.blocked = new Uint8Array(map.cols * map.rows);
  }

  /**
   * Refresh the solid-obstacle overlay from the world. Buildings, wreckage and
   * features are stamped as blocked cells so fields route around them. Only
   * rebuilds (and clears the field cache) when the obstacle layout actually
   * changed, detected via a stable signature — cheap on most ticks.
   */
  updateObstacles(w: World): void {
    const cs = this.map.cellSize;
    const cols = this.map.cols;
    let sig = FNV_OFFSET >>> 0;
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e] || !blocksField(w, e)) continue;
      const cx = (w.posX[e] / cs) | 0;
      const cy = (w.posY[e] / cs) | 0;
      sig = mixHash(sig, cy * cols + cx);
      sig = mixHash(sig, w.def(e).radius | 0);
    }
    if (this.builtOnce && sig === this.blockedSig) return;
    this.blockedSig = sig;
    this.builtOnce = true;
    this.blocked.fill(0);
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e] || !blocksField(w, e)) continue;
      this.stampDisc(w.posX[e], w.posY[e], w.def(e).radius);
    }
    this.cache.clear();
  }

  private stampDisc(x: number, y: number, radius: number): void {
    const cs = this.map.cellSize;
    const cols = this.map.cols;
    const rows = this.map.rows;
    const rr = radius + BLOCK_INFLATE;
    const minCx = Math.max(1, ((x - rr) / cs) | 0);
    const maxCx = Math.min(cols - 2, ((x + rr) / cs) | 0);
    const minCy = Math.max(1, ((y - rr) / cs) | 0);
    const maxCy = Math.min(rows - 2, ((y + rr) / cs) | 0);
    const rr2 = rr * rr;
    for (let cy = minCy; cy <= maxCy; cy++) {
      for (let cx = minCx; cx <= maxCx; cx++) {
        const ccx = (cx + 0.5) * cs;
        const ccy = (cy + 0.5) * cs;
        const ex = ccx - x;
        const ey = ccy - y;
        if (ex * ex + ey * ey <= rr2) this.blocked[cy * cols + cx] = 1;
      }
    }
  }

  get(goalX: number, goalY: number, tick: number): FlowField {
    const gcx = this.map.worldToCellX(goalX);
    const gcy = this.map.worldToCellY(goalY);
    const key = gcy * this.map.cols + gcx;
    let f = this.cache.get(key);
    if (!f) {
      f = new FlowField(this.map, gcx, gcy, this.blocked);
      if (this.cache.size >= this.cap) {
        // Evict least-recently-used.
        let oldestKey = -1;
        let oldestTick = Infinity;
        for (const [k, v] of this.cache) {
          if (v.lastUsedTick < oldestTick) {
            oldestTick = v.lastUsedTick;
            oldestKey = k;
          }
        }
        if (oldestKey >= 0) this.cache.delete(oldestKey);
      }
      this.cache.set(key, f);
    }
    f.lastUsedTick = tick;
    return f;
  }
}
